import { Row, RowItemType, Section, SectionType, SelectorIdentifier, UIElementType } from '../../models';
import { calculateMaintenanceInterval, convertDate, createSmartTranslationMessage } from '../../models/utils';
import { LocationCommunicationType, MonitorSeriesNames } from '../../../location-card.constants';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import {
    loadInstallationTypes,
    setGlobalConfig,
    removeSection,
    pickMapCoords,
    openModemProviderDialog,
    statusChange,
    attachSectionToRow,
    enableField,
    disableField,
    tagKeyChange,
    setFieldValueAction,
    evaluateScanRateLock,
} from '../../actions';
import { EchoSectionIds, LongRangeFieldIds } from './base-config-models';
import { LocationCardStatus, SampleRateElement, ScanRateElement } from '../../../models/location-card.model';
import { DEFAULT_APN_PROVIDER, QMANNING_ENTITY } from 'app/shared/constant';
import { ConfigureAlarmsFieldId, InstallationTypeSectionIds } from '../common.models';
import { BasicInfoCommonFieldIds } from '../common.models';
import { ValidatorIdentifier } from '../../models/validator-identifier';
import { field, FieldActions } from '../../core/field-schema';
import { required, maxLength, pattern, custom } from '../../core/validator-builders';
import { Selectable } from 'app/shared/models/selectable';
import { InstallationSeries, InstallationType } from 'app/shared/models/location-details';
import { pipeInstallationConfig } from './installation-types/pipe';
import { weirInstallationConfig } from './installation-types/weir';
import { flumeInstallationConfig } from './installation-types/flume';
import { channelInstallationConfig } from './installation-types/channel';
import { EntityResponseItem } from 'app/shared/models/view-data';
import { EchoLocationCardState } from './models';
import { TextField, SelectField } from '../../models/field-models';
import { LocationStatus, RainLocation } from 'app/shared/models/locations';
import { Action } from '@ngrx/store';

export const customPropertyRowGenerator = (index: number, populateValues?: { key: string; value: string }): Row => {
    return {
        info: {
            id: `custom-property-${index}`,
            panelClass: 'custom-50p',
        },
        items: [
            field.autocomplete<string, string>(`${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`, 'key', {
                optionsSelector: SelectorIdentifier.TagKeyAutocomplete,
                value: populateValues?.key,
                placeholder: 'ADD_EDIT.INPUT_TAG_NAME',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
                displayMapper: (option) => option,
                valueMapper: (option) => option,
                events: {
                    onBlur: (value) => [
                        tagKeyChange({
                            fieldId: `${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`,
                            newValue: value,
                            isMp1: true,
                        }),
                    ],
                },
            }),

            field.text(`${BasicInfoCommonFieldIds.CustomPropertyValue}-${index}`, 'value', {
                value: populateValues?.value,
                placeholder: 'ADD_EDIT.INPUT_TAG_VALUE',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
            }),
        ],
    };
};

/**
 * Echo form configuration
 */
export const basicInfo = (state: EchoLocationCardState): Section => {
    const payload = state.locationDetails && state.locationDetails.length ? state.locationDetails[0] : null;
    const monitorConfig = state.monitorConfiguration;
    const settings = state.settings;

    const rainGauge =
        payload && payload.assignedRainGaugeLocationId
            ? state.options.rainGauges.data.find((v) => v.locationId === payload.assignedRainGaugeLocationId)
            : null;
    const isEditMode = !!payload;
    const isSwitchMonitor = payload && payload.series !== MonitorSeriesNames.echo;

    const isScanRateLocked =
        isEditMode &&
        !isSwitchMonitor &&
        monitorConfig.samplerate != null &&
        monitorConfig.fastrate != null &&
        monitorConfig.samplerate === monitorConfig.fastrate;

    const showCoordsButton = state.locationCardData.enableSelectOnMap;
    const basicInfoConfig: Section = {
        info: {
            id: EchoSectionIds.BasicInfo,
            title: 'ADD_EDIT.ADD_EDIT_INFORMATION',
            displayTitle: true,
            expanded: true,
            expandable: true,
            panelClass: 'basic-info-section',
        },
        rows: [
            // Row 1: Location Name and Series
            {
                info: {
                    id: 'basic-info-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.LocationName, 'locationName', {
                        value: isEditMode ? payload.locationName : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_NAME',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            custom({
                                identifier: ValidatorIdentifier.UniqueLocationName,
                                message: 'COMMON.LOCATION_NAME_EXISTS',
                            }),
                            maxLength({
                                value: 63,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.trim().charAt(0).toUpperCase() + value.trim().slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    field.select<LocationCardMonitorSeries, string>(BasicInfoCommonFieldIds.Series, 'series', {
                        panelClass: 'custom-width',
                        value: MonitorSeriesNames.echo,
                        placeholder: 'COMMON.MONITOR_SERIES_PLACEHOLDER',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MonitorSeriesOptions,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: function (
                                this: SelectField<LocationCardMonitorSeries, string> & {
                                    _actions: FieldActions<string>;
                                },
                                value,
                            ) {
                                return [
                                    enableField({ fieldId: BasicInfoCommonFieldIds.InstallationType }),
                                    loadInstallationTypes({ installationId: value.monitorSeriesID }),
                                    setGlobalConfig({ monitorSeriesName: value.value }),
                                    setFieldValueAction({
                                        fieldId: BasicInfoCommonFieldIds.QFinalEntityID,
                                        value: QMANNING_ENTITY,
                                    }),
                                ];
                            },
                        },
                    }),
                ],
            },

            // Row 2: Serial Number and Status
            {
                info: {
                    id: 'basic-info-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.SerialNumber, 'serialnumber', {
                        panelClass: 'custom-width',
                        value: isEditMode ? payload.serialNumber : null,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SERIAL',
                        validators: [
                            required(),
                            maxLength({ value: 5 }),
                            pattern({
                                value: REGEX_CONFIG.numericOnlyPattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_ERROR',
                            }),
                        ],
                        events: {
                            onInput: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.replace(/[^0-9]/g, '');
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.StatusSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'status-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'status-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'status-row',
                                        panelClass: 'status-inner-row flex align-items-center gap-4',
                                    },
                                    items: [
                                        field.select<LocationCardStatus, number>(
                                            BasicInfoCommonFieldIds.Status,
                                            'status',
                                            {
                                                value: isEditMode ? payload.status : 0,
                                                panelClass: 'custom-width',
                                                disabled: !isEditMode,
                                                label: 'Status',
                                                valueMapper: (option) => option.key,
                                                displayMapper: (option) => option.name,
                                                validators: [required()],
                                                optionsSelector: SelectorIdentifier.LocationStatuses,
                                                events: {
                                                    onChange: () => [statusChange({ isMp1: true })],
                                                },
                                            },
                                        ),
                                        field.select<number, number>(
                                            BasicInfoCommonFieldIds.MaintenanceInterval,
                                            'maintenanceInterval',
                                            {
                                                value: isEditMode
                                                    ? calculateMaintenanceInterval(payload.maintenanceEnd)
                                                    : 2,
                                                label: 'CUSTOMER_EDITOR.HOURS',
                                                valueMapper: (option) => option,
                                                displayMapper: (option) => String(option),
                                                panelClass: 'width-15',
                                                validators: [required()],
                                                visible: isEditMode
                                                    ? payload.status === LocationStatus.Maintenance
                                                    : false,
                                                optionsSelector: SelectorIdentifier.MaintenanceIntervals,
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.MaintenanceEnd,
                                            name: 'maintenanceEnd',
                                            panelClass: 'width-25 field-status-info',
                                            label: isEditMode
                                                ? createSmartTranslationMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE', {
                                                    date: convertDate(payload.maintenanceEnd, state.customer?.dateFormat, state.customer?.timeFormat)
                                                  })
                                                : 'CUSTOMER_EDITOR.HOURS',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Text,
                                            visible: isEditMode ? payload.status === LocationStatus.Maintenance : false,
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 3: Communication and IP Address
            {
                info: {
                    id: 'basic-info-row-4',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<LocationCardMonitorSeries, string>(
                        BasicInfoCommonFieldIds.CommunicationType,
                        'communicationType',
                        {
                            value: isEditMode
                                ? String(payload.communicationType)
                                : String(LocationCommunicationType.Wireless),
                            placeholder: 'COMMON.COMMUNICATION_TYPE_PLACEHOLDER',
                            validators: [required()],
                            panelClass: 'custom-width',
                            optionsSelector: SelectorIdentifier.CommunicationTypes,
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            events: {
                                onChange: (option) => {
                                    const isWireless = Number(option.value) === LocationCommunicationType.Wireless;
                                    return [
                                        isWireless
                                            ? enableField({ fieldId: BasicInfoCommonFieldIds.IpAddress })
                                            : disableField({ fieldId: BasicInfoCommonFieldIds.IpAddress }),
                                    ];
                                },
                            },
                        },
                    ),
                    field.text(BasicInfoCommonFieldIds.IpAddress, 'ipaddress', {
                        value: isEditMode ? payload.connectionString : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_IP',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.ipaddressPattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.IP_ADDRESS_ERROR',
                            }),
                            maxLength({ value: 100 }),
                        ],
                        disabled: isEditMode ? payload.communicationType !== LocationCommunicationType.Wireless : false,
                    }),
                ],
            },

            // Row 4: APN Provider and Description
            {
                info: {
                    id: 'basic-info-row-5',
                    panelClass: 'custom-50p',
                },
                items: [
                    {
                        id: 'modem-provider-section',
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'modem-provider-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'modem-provider-row',
                                        panelClass: 'flex custom-flat-button',
                                    },
                                    items: [
                                        field.select<Selectable, string>(
                                            BasicInfoCommonFieldIds.ApnProvider,
                                            'apnProvider',
                                            {
                                                value: isEditMode && !isSwitchMonitor ? monitorConfig.apn : DEFAULT_APN_PROVIDER,
                                                valueMapper: (option) => option.name,
                                                displayMapper: (option) => option.name,
                                                panelClass: 'full-width',
                                                placeholder: 'COMMON.MODEM_PROVIDER',
                                                validators: [required()],
                                                optionsSelector: SelectorIdentifier.ModemProviders,
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.ApnProviderBtn,
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Button,
                                            name: 'modemProviderBtn',
                                            iconName: 'edit',
                                            tooltip: 'COMMON.EDIT_TOOLTIP',
                                            panelClass: 'no-margin',
                                            events: {
                                                onClick: () => [openModemProviderDialog()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    field.text(BasicInfoCommonFieldIds.Description, 'description', {
                        value: isEditMode ? payload.description : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LOCATION',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 257,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                ],
            },

            // Row 5: Rain Gauge and Manhole Depth
            {
                info: {
                    id: 'location-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.autocomplete<RainLocation, string>(
                        BasicInfoCommonFieldIds.AssignedRainGauge,
                        'assignedRainGauge',
                        {
                            value: rainGauge ? rainGauge.locationName : null,
                            autoClearOnFocus: true,
                            panelClass: 'custom-width',
                            label: 'Assigned Rain Gauge',
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                            clearable: true,
                            optionsSelector: SelectorIdentifier.RainGaugeAutocomplete,
                            displayMapper: (option) => option.locationName,
                            valueMapper: (option) => option.locationName,
                            validators: [
                                maxLength({
                                    value: 50,
                                }),
                            ],
                        },
                    ),
                    field.number(BasicInfoCommonFieldIds.ManholeDepth, 'manholedepth', {
                        value: isEditMode ? payload.manholeDepth : null,
                        panelClass: 'custom-width',
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.MANHOLE_DEPTH',
                        placeholderSuffix: settings.depthUnit,
                        validators: [
                            required(),
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.numeric0_100000With1DecimalPlacesPattern
                                    : REGEX_CONFIG.numeric0_100000With2DecimalPlacesPattern,
                                message: settings.isMetric
                                    ? 'COMMON.NUMERIC_PRECISION_ERROR_METRIC'
                                    : 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                            maxLength({
                                value: 50,
                            }),
                            custom({
                                identifier: ValidatorIdentifier.ManholeDepthToPipeHeightValidator,
                                message: 'COMMON.MAN_HOLE_DEPTH_PIPE_HEIGHT_ERROR',
                            }),
                        ],
                        events: {
                            onChange: (value) => [
                                setFieldValueAction({ fieldId: LongRangeFieldIds.ManholeDepth, value }),
                            ],
                        },
                    }),
                ],
            },

            // Row 6: Manhole Address and Coordinates
            {
                info: {
                    id: 'location-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.ManholeAddress, 'manholeaddress', {
                        value: isEditMode ? payload.manholeAddress : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MANHOLE_ADDRESS',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 64,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.CoordinatesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CoordinatesSection,
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'coordinates-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'coordinates-row',
                                        panelClass: `coordinates-inner-row ${
                                            showCoordsButton ? 'flex-end' : 'justify-space-between'
                                        }`,
                                    },
                                    items: [
                                        field.number(BasicInfoCommonFieldIds.Latitude, 'latitude', {
                                            value: isEditMode ? payload.latitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LATITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.latitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        field.number(BasicInfoCommonFieldIds.Longitude, 'longitude', {
                                            value: isEditMode ? payload.longitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LONGITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.longitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: 'move-location-icon',
                                            name: 'moveLocationIcon',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Image,
                                            visible: !!showCoordsButton,
                                            imgSrc: '/assets/images/icons/move_icon.png',
                                            imgHeight: 18,
                                            imgWidth: 18,
                                            panelClass: 'move-icon',
                                            events: {
                                                onClick: () => [pickMapCoords()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 7: Sample Rates
            {
                info: {
                    id: 'settings-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.SampleRate, 'samplerate', {
                        value: isEditMode && !isSwitchMonitor ? monitorConfig.samplerate : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SAMPLE_RATE',
                        validators: [required(), custom({ identifier: ValidatorIdentifier.NormalRateValidator })],
                        optionsSelector: SelectorIdentifier.SampleRates,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: () => [evaluateScanRateLock()],
                        },
                    }),
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.FastRate, 'fastrate', {
                        value: isEditMode && !isSwitchMonitor ? monitorConfig.fastrate : null,
                        panelClass: 'custom-width',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FAST_SAMPLE_RATE',
                        validators: [
                            required(),
                            custom({
                                identifier: ValidatorIdentifier.FastRateValidator,
                                message: 'COMMON.TRITON_SAMPLE_RATE_ERROR',
                            }),
                        ],
                        optionsSelector: SelectorIdentifier.SampleRates,
                        events: {
                            onChange: () => [evaluateScanRateLock()],
                        },
                    }),
                ],
            },

            // Row Scan Rate
            {
                info: {
                    id: 'settings-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<ScanRateElement, number>(BasicInfoCommonFieldIds.ScanRate, 'scanRate', {
                        value: isScanRateLocked ? 0 : isEditMode && !isSwitchMonitor ? monitorConfig.scanRate ?? 0 : 0,
                        disabled: isScanRateLocked,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SCAN_RATE',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.ScanRates,
                        valueMapper: (option) => option.id,
                        displayMapper: (option) => option.text,
                    }),
                ],
            },

            // Row 8: Time Settings
            {
                info: {
                    id: 'settings-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(BasicInfoCommonFieldIds.SyncTime, 'synctime', {
                        value: isEditMode && !isSwitchMonitor ? monitorConfig.synctime : true,
                        panelClass: 'custom-width item-flex-center',
                        label: 'ADD_EDIT.CLOCK_SYNCHRONIZATON',
                    }),
                    field.toggle(BasicInfoCommonFieldIds.UseDaylightSaving, 'usedaylightsaving', {
                        value: isEditMode && !isSwitchMonitor ? monitorConfig.usedaylightsaving : true,
                        panelClass: 'custom-width item-flex-center',
                        label: 'ADD_EDIT.DAYLIGHT_SAVING',
                    }),
                ],
            },

            // Row 9: Installation Date
            {
                info: {
                    id: 'settings-row-3',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.date(BasicInfoCommonFieldIds.InstallationDate, 'installationDate', {
                        label: 'Installation Date',
                        placeholder: 'Select date',
                        dateFormat: settings.dateFormat,
                        datePickerType: 'date',
                        timeInterval: 1,
                        panelClass: 'full-width',
                        value: isEditMode ? payload.installationDate : new Date(),
                    }),
                ],
            },

            // Row 10: Installation Type and QFinal
            {
                info: {
                    id: 'installation-type-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<InstallationType, string>(
                        BasicInfoCommonFieldIds.InstallationType,
                        'installationType',
                        {
                            preserveOldValues: false,
                            value:
                                isEditMode && !isSwitchMonitor
                                    ? `${payload.installationType}: ${payload.installationShapeTypeID}`
                                    : '',
                            placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.INSTALLATION_TYPE',
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.InstallationTypes,
                            panelClass: 'custom-width',
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            events: {
                                onChange: (value) => {
                                    const toDispatch: Action[] = [setFieldValueAction({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold, value: null})];
                                    const installationSections = [
                                        InstallationTypeSectionIds.Pipe,
                                        InstallationTypeSectionIds.Weir,
                                        InstallationTypeSectionIds.Flume,
                                        InstallationTypeSectionIds.Channel,
                                    ];

                                    installationSections.forEach((sectionId) => {
                                        toDispatch.push(
                                            removeSection({ sectionId, parentSectionId: EchoSectionIds.BasicInfo }),
                                        );
                                    });

                                    // For Pipe type (Series 200)
                                    if (value.series === InstallationSeries.Pipe) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EchoSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Pipe,
                                                sectionConfig: pipeInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Weir) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EchoSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Weir,
                                                sectionConfig: weirInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Flume) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EchoSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Flume,
                                                sectionConfig: flumeInstallationConfig(state),
                                            }),
                                        );
                                    } else if (value.series === InstallationSeries.Channel) {
                                        toDispatch.push(
                                            attachSectionToRow({
                                                targetSectionId: EchoSectionIds.BasicInfo,
                                                rowId: 'installation-type-row',
                                                fieldId: InstallationTypeSectionIds.Channel,
                                                sectionConfig: channelInstallationConfig(state),
                                            }),
                                        );
                                    }

                                    return toDispatch;
                                },
                            },
                        },
                    ),
                    field.select<EntityResponseItem, number>(BasicInfoCommonFieldIds.QFinalEntityID, 'qFinalEntityID', {
                        value: isEditMode ? payload.qFinalEntityID : QMANNING_ENTITY,
                        placeholder: 'COMMON.QFINALTEXT',
                        panelClass: 'custom-width',
                        optionsSelector: SelectorIdentifier.QFinalEntities,
                        valueMapper: (option) => option.entityId,
                        displayMapper: (option) => option.entityName,
                    }),
                ],
            },

            // Custom properties section
            {
                info: {
                    id: 'custom-properties-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                                title: 'ADD_EDIT.TAGS_SECTION_NAME',
                                displayTitle: false,
                                displayActionBtns: true,
                                type: SectionType.Array,
                                expanded: false,
                                expandable: false,
                                itemGenerator: customPropertyRowGenerator,
                                panelClass: 'custom-properties-panel',
                                maxItems: 64,
                            },
                            rows:
                                isEditMode && payload.customProperties
                                    ? payload.customProperties.map((item, index) =>
                                          customPropertyRowGenerator(index, item),
                                      )
                                    : [],
                        },
                    },
                ],
            },
        ],
    };

    if (!isEditMode || isSwitchMonitor) {
        return basicInfoConfig;
    }

    const installationConfigGenerator = pickInstallationTypeConfig(payload.installationShapeTypeID);

    if (!installationConfigGenerator) {
        return basicInfoConfig;
    }

    // Echo passes only state to installation config generator (no second param)
    const installationConfig = installationConfigGenerator(state);
    const sectionId = getInstallationTypeSectionId(payload.installationShapeTypeID);

    const installationTypeRowIndex = basicInfoConfig.rows.findIndex((row) => row.info.id === 'installation-type-row');
    basicInfoConfig.rows.splice(installationTypeRowIndex + 1, 0, {
        info: { id: sectionId, panelClass: 'full-width' },
        items: [{ id: sectionId, type: RowItemType.Section, section: installationConfig }],
    });

    return basicInfoConfig;
};

function pickInstallationTypeConfig(installationSeries: number) {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return pipeInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return weirInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Channel) {
        return flumeInstallationConfig;
    } else if (installationSeries === InstallationSeries.Channel) {
        return channelInstallationConfig;
    }
}

function getInstallationTypeSectionId(installationSeries: number) {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return InstallationTypeSectionIds.Pipe;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return InstallationTypeSectionIds.Weir;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Channel) {
        return InstallationTypeSectionIds.Flume;
    } else if (installationSeries === InstallationSeries.Channel) {
        return InstallationTypeSectionIds.Channel;
    }
}
